<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxTabs } from 'devextreme-vue/tabs';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxCheckBox } from 'devextreme-vue/check-box';

import type { CurrencyOption } from '../api/services/currencies';

const props = defineProps<{
  currencies: string[];
  currentCurrency: string;
  availableCurrencyOptions: CurrencyOption[];
  adding?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:currentCurrency', v: string): void;
  (e: 'add', payload: { currency: string; copyFrom?: string }): void;
}>();

const addDialogOpen = ref(false);
const newCurrency = ref<string | null>(null);
const copyEnabled = ref(false);
const sourceCurrency = ref<string | null>(null);

const availableToAdd = computed(() =>
  props.availableCurrencyOptions.filter((c) => !props.currencies.includes(c.value)),
);

const sourceOptions = computed(() =>
  props.currencies.map((c) => ({ value: c, text: c })),
);

const currencyOptions = computed(() => availableToAdd.value);

watch(addDialogOpen, (open) => {
  if (!open) return;
  newCurrency.value = availableToAdd.value[0]?.value ?? null;
  copyEnabled.value = props.currencies.length > 0;
  sourceCurrency.value = props.currentCurrency || props.currencies[0] || null;
});

function openAddDialog(): void {
  if (!availableToAdd.value.length) return;
  addDialogOpen.value = true;
}

function confirmAdd(): void {
  if (!newCurrency.value) return;
  emit('add', {
    currency: newCurrency.value,
    copyFrom: copyEnabled.value && sourceCurrency.value ? sourceCurrency.value : undefined,
  });
  addDialogOpen.value = false;
}

function onTabChanged(currency: string): void {
  emit('update:currentCurrency', currency);
}
</script>

<template>
  <div class="tariff-currency-tabs">
    <DxTabs
      v-if="currencies.length"
      :items="currencies"
      :selected-item="currentCurrency"
      @update:selected-item="onTabChanged"
    />
    <span v-else class="tct-empty-hint">Валюты не добавлены</span>
    <DxButton
      icon="add"
      text="Валюта"
      type="default"
      styling-mode="outlined"
      :disabled="!availableToAdd.length || adding"
      @click="openAddDialog"
    />
  </div>

  <DxPopup
    v-model:visible="addDialogOpen"
    title="Добавить валюту"
    :show-close-button="true"
    :drag-enabled="false"
    width="420"
    height="auto"
  >
    <div class="tct-dialog">
      <label class="tct-field">
        <span>Валюта</span>
        <DxSelectBox
          v-model:value="newCurrency"
          :items="currencyOptions"
          display-expr="text"
          value-expr="value"
          placeholder="Выберите валюту"
          :search-enabled="true"
        />
      </label>

      <template v-if="currencies.length">
        <DxCheckBox
          v-model:value="copyEnabled"
          text="Скопировать цены из другой валюты"
        />
        <label v-if="copyEnabled" class="tct-field">
          <span>Исходная валюта</span>
          <DxSelectBox
            v-model:value="sourceCurrency"
            :items="sourceOptions"
            display-expr="text"
            value-expr="value"
          />
        </label>
        <p v-if="copyEnabled" class="tct-hint">
          Цены будут сконвертированы по курсу через API.
        </p>
      </template>
    </div>

    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: 'Добавить',
        type: 'default',
        stylingMode: 'contained',
        disabled: !newCurrency || adding,
        onClick: confirmAdd,
      }"
    />
    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{ text: 'Отмена', stylingMode: 'outlined', onClick: () => (addDialogOpen = false) }"
    />
  </DxPopup>
</template>

<style scoped>
.tariff-currency-tabs {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.tct-empty-hint { font-size: 13px; color: #9ca3af; }
.tct-dialog { display: flex; flex-direction: column; gap: 14px; padding: 8px 4px 0; }
.tct-field { display: flex; flex-direction: column; gap: 6px; font-size: 12px; color: #555; }
.tct-hint { margin: 0; font-size: 12px; color: #6b7280; }
</style>
